// İnen güncelleme, uygulamaya geri dönüldüğü an devreye girer (8.6 · D-703).
//
// KUSUR. expo-updates yeni paketi açılışta arka planda indirir ama ancak
// BİR SONRAKİ soğuk açılışta uygular. Batuhan alt çubuk değişikliğini telefonda
// göremedi; iki kez tamamen kapatıp açınca gördü. Her güncellemede herkes aynı
// şeyi yaşıyordu. Batuhan: "memnuniyetle."
//
// KURAL. Paket indirilmiş ve bekliyorsa, üye uygulamaya geri döndüğü anda
// yeniden yüklenir — ama yalnız kimsenin bir şey kaybetmeyeceği bir anda:
// - Bir arama sürmüyorsa: yeniden yükleme aramayı keserdi.
// - Beş ana sekmeden birindeyse: sohbette yazılmış ama gönderilmemiş metin
//   saklanmıyor, yeniden yükleme onu silerdi.
// - En az 30 saniye uzakta kalındıysa: bir şeyi kopyalayıp hemen dönen birinin
//   ekranı elinden alınmasın.
// Koşullar tutmazsa bir sonraki dönüş beklenir; soğuk açılış zaten uygular.
//
// Dönüşte ayrıca yeni bir paket var mı diye bakılır (en fazla 10 dakikada bir)
// ve varsa indirilir; böylece uzun süre açık kalan uygulama da güncel kalır.
import { useEffect, useRef } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import * as Updates from 'expo-updates';

export const MIN_AWAY_MS = 30_000;
export const CHECK_INTERVAL_MS = 10 * 60_000;

/** Yeniden yüklemenin güvenli olduğu ekranlar: beş ana sekme. */
export const SAFE_SCREENS: ReadonlySet<string> = new Set(['discover', 'inbox', 'callHistory', 'calendar', 'ownProfile']);

export function shouldReloadOnResume(input: { pending: boolean; safe: boolean; awayMs: number }): boolean {
  return input.pending && input.safe && input.awayMs >= MIN_AWAY_MS;
}

type UpdatesLike = {
  isEnabled: boolean;
  reloadAsync: () => Promise<void>;
  checkForUpdateAsync: () => Promise<{ isAvailable: boolean }>;
  fetchUpdateAsync: () => Promise<unknown>;
};

type AppStateLike = {
  addEventListener: (type: 'change', listener: (state: AppStateStatus) => void) => { remove(): void };
};

export function useApplyUpdateOnResume(options: {
  pending: boolean;
  safe: boolean;
  updates?: UpdatesLike;
  appState?: AppStateLike;
  now?: () => number;
}) {
  const updates = options.updates ?? (Updates as unknown as UpdatesLike);
  const appState = options.appState ?? AppState;
  const now = options.now ?? Date.now;
  const latest = useRef({ pending: options.pending, safe: options.safe });
  latest.current = { pending: options.pending, safe: options.safe };
  const leftAt = useRef<number | null>(null);
  const lastCheck = useRef(0);

  useEffect(() => {
    if (!updates.isEnabled) return undefined;
    const subscription = appState.addEventListener('change', (state) => {
      if (state === 'background') {
        leftAt.current = now();
        return;
      }
      if (state !== 'active' || leftAt.current === null) return;
      const awayMs = now() - leftAt.current;
      leftAt.current = null;
      if (shouldReloadOnResume({ ...latest.current, awayMs })) {
        updates.reloadAsync().catch(() => {});
        return;
      }
      if (now() - lastCheck.current < CHECK_INTERVAL_MS) return;
      lastCheck.current = now();
      updates.checkForUpdateAsync()
        .then((result) => (result.isAvailable ? updates.fetchUpdateAsync() : undefined))
        .catch(() => {});
    });
    return () => subscription.remove();
  }, [appState, now, updates]);
}
